import { useMemo } from 'react';
import { QueryClient, useQuery } from '@tanstack/react-query';
import { isNil, toString } from 'lodash';
import { MeasurementDatesService } from 'api';
import {
  MEASUREMENT_DATES_BY_COMPANY_DEFAULT_ERROR_MESSAGE,
  MEASUREMENT_DATES_BY_COMPANY_QUERY_KEY,
  SERVICES_COMPANIES_REFETCH_ON_WINDOW_FOCUS,
} from 'common/constants/services/companies';
import { captureException } from 'scalarAnalytics/postHog';
import { throwError } from 'utilities';
import {
  GetMeasurementDatesByCompanyParams,
  GetMeasurementDatesByCompanyResponse,
  GetMeasurementDatesByCompanyReturn,
  UseGetMeasurementDatesByCompanyParams,
} from './useGetMeasurementDatesByCompany.types';

export const getMeasurementDatesByCompany = async (
  params: GetMeasurementDatesByCompanyParams
): Promise<GetMeasurementDatesByCompanyResponse | null> => {
  const { companyId } = params;

  if (isNil(companyId)) {
    return throwError(MEASUREMENT_DATES_BY_COMPANY_DEFAULT_ERROR_MESSAGE);
  }

  try {
    const measurementDates = await MeasurementDatesService.apiMeasurementDatesCompanyRead(toString(companyId));

    if (!measurementDates) {
      return throwError(MEASUREMENT_DATES_BY_COMPANY_DEFAULT_ERROR_MESSAGE);
    }

    return measurementDates;
  } catch (error) {
    return throwError(MEASUREMENT_DATES_BY_COMPANY_DEFAULT_ERROR_MESSAGE, error);
  }
};

// Refreshes on-screen company dates without a fetching state, which nulls the list and reloads
// date-dependent pages. Unchanged dates keep their references, so a no-op refresh re-renders nothing.
export const refreshMeasurementDatesByCompany = (queryClient: QueryClient) =>
  Promise.all(
    queryClient
      .getQueriesData<GetMeasurementDatesByCompanyResponse | null>({
        queryKey: [MEASUREMENT_DATES_BY_COMPANY_QUERY_KEY],
        type: 'active',
      })
      .map(async ([queryKey]) => {
        const companyId = queryKey[1] as GetMeasurementDatesByCompanyParams['companyId'];

        if (isNil(companyId)) {
          return;
        }

        try {
          const measurementDates = await getMeasurementDatesByCompany({ companyId });
          // A 401 resolves to null rather than throwing; never blank out the cached dates.
          if (measurementDates) {
            queryClient.setQueryData(queryKey, measurementDates);
          }
        } catch (error) {
          // The save already succeeded; only the nav flag is stale until the next load.
          // eslint-disable-next-line no-console
          console.error('Could not refresh measurement dates', error);
          captureException(error, { source: 'measurement_dates_refresh' });
        }
      })
  );

const useGetMeasurementDatesByCompany = (
  params: UseGetMeasurementDatesByCompanyParams
): GetMeasurementDatesByCompanyReturn => {
  const { companyId, shouldQueryAutomatically = true } = params;

  const { data, error, isError, isFetching, isLoading, refetch, fetchStatus } = useQuery<
    GetMeasurementDatesByCompanyReturn['data'],
    Error
  >({
    enabled: !isNil(companyId) && shouldQueryAutomatically,
    queryFn: () => getMeasurementDatesByCompany({ companyId }),
    queryKey: [MEASUREMENT_DATES_BY_COMPANY_QUERY_KEY, companyId],
    refetchOnWindowFocus: SERVICES_COMPANIES_REFETCH_ON_WINDOW_FOCUS,
  });

  const successData = useMemo(() => {
    if (fetchStatus === 'idle') {
      return data;
    }

    return null;
  }, [data, fetchStatus]);

  return {
    data: successData,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetMeasurementDatesByCompany;
